function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
import React from "react";
import PropTypes from "prop-types";
import { responsivePropType, responsiveMarginType } from "../hooks/useResponsiveProp";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import useTextStyle from "../hooks/useTextStyle";
import useBackground from "../hooks/useBackground";
import { responsiveMargin, responsiveTextStyle } from "../utils/css";
import { mergeProps } from "../utils/component";
import { hasOwnProperty } from "../utils/core";
import { TEXT_STYLES, TEXT_ALIGNS } from "../utils/constants";
import { formatArray } from "../utils/array";
import { jsx as ___EmotionJSX } from "@emotion/core";
var AS = ["h1", "h2", "h3", "h4", "h5", "h6", "p"];
var COLORS = ["black", "white", "grey.t75", "primary.blue.t100", "highlight.blue.t100", "conditional.positive.text", "conditional.negative.text"];
var ALIGNS = TEXT_ALIGNS;
var allowedColors = [{
  textStyles: ["hero", "heading1", "heading2", "heading3", "heading4", "heading5", "heading6"],
  allowedColors: ["black", "white", "primary.blue.t100"]
}, {
  textStyles: ["subtitle1", "subtitle2", "body1", "body2", "legal"],
  allowedColors: ["black", "white", "grey.t75", "primary.blue.t100", "highlight.blue.t100", "conditional.positive.text", "conditional.negative.text"]
}, {
  textStyles: ["overline"],
  allowedColors: ["black", "white", "grey.t75", "primary.blue.t100", "highlight.blue.t100"]
}];
function getInheritedColor(backgroundColor) {
  return backgroundColor === "primary.blue.t100" ? "white" : "black";
}
var DEFAULT_PROPS = {
  as: "p",
  textStyle: "body1",
  color: "black",
  align: "inherit",
  wrap: true
};
Text.AS = AS;
Text.TEXT_STYLES = TEXT_STYLES;
Text.COLORS = COLORS;
Text.ALIGNS = ALIGNS;
Text.allowedColors = allowedColors;
Text.DEFAULT_PROPS = DEFAULT_PROPS;
function Text(props) {
  var _useTextStyle = useTextStyle(),
    inheritedTextStyle = _useTextStyle.textStyle;
  var _useBackground = useBackground(),
    bgMap = _useBackground.bgMap;
  var inheritedProps = {
    textStyle: inheritedTextStyle
  };
  var mergedProps = mergeProps(props, DEFAULT_PROPS, inheritedProps, {
    id: function id(_id) {
      return typeof _id === "string";
    },
    as: function as(_as) {
      return AS.includes(_as);
    },
    textStyle: function textStyle(_textStyle) {
      return TEXT_STYLES.includes(_textStyle);
    },
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    align: function align(_align) {
      return ALIGNS.includes(_align);
    },
    wrap: function wrap(_wrap) {
      return typeof _wrap === "boolean";
    }
  });
  var id = mergedProps.id,
    as = mergedProps.as,
    align = mergedProps.align,
    wrap = mergedProps.wrap,
    children = mergedProps.children,
    testId = mergedProps.testId;
  var css = useResponsivePropsCSS(mergedProps, DEFAULT_PROPS, {
    color: function color(_, theme, bp) {
      var color = hasOwnProperty(props, "color") && hasOwnProperty(mergedProps, "color") ? mergedProps.color : getInheritedColor(bgMap === null || bgMap === void 0 ? void 0 : bgMap[bp]);
      return theme.text.getCSS({
        color: color,
        wrap: wrap,
        align: align
      });
    },
    margin: responsiveMargin,
    textStyle: responsiveTextStyle
  });
  var Component = as;
  return ___EmotionJSX(Component, {
    id: id,
    css: css,
    "data-testid": testId
  }, children);
}
Text.propTypes = _objectSpread(_objectSpread(_objectSpread({
  id: PropTypes.string,
  as: PropTypes.oneOf(AS)
}, responsiveMarginType), responsivePropType("textStyle", PropTypes.oneOf(TEXT_STYLES))), {}, {
  color: function color(props) {
    allowedColors.forEach(function (_ref) {
      var textStyles = _ref.textStyles,
        allowedColors = _ref.allowedColors;
      if (textStyles.includes(props.textStyle) && !allowedColors.includes(props.color)) {
        return new Error("Text: color=\"".concat(props.color, "\" is not allowed for textStyle=\"").concat(props.textStyle, "\". Must be one of: ").concat(formatArray(allowedColors)));
      }
    });
  },
  align: PropTypes.oneOf(ALIGNS),
  wrap: PropTypes.bool,
  children: PropTypes.node,
  testId: PropTypes.string
});
export default Text;